import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, X } from "lucide-react";
import { authApi } from "../lib/authApi";
import { useAuth } from "../lib/authContext";
import {
  inventoryLocalData, isSyncAlreadyDone, markSyncDone,
} from "../lib/localDataMigration";
import { journalApi } from "../lib/journal";

/**
 * Watches authenticated state. When a user logs in for the first time
 * and there is local data, prompts to sync.  Single-use per (user, device).
 */
export default function MigrationModal() {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snapshot, setSnapshot] = useState({ total: 0 });

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (isSyncAlreadyDone(user.id)) return;
    const inv = inventoryLocalData();
    if (!inv.total) {
      markSyncDone(user.id);
      return;
    }
    setSnapshot(inv);
    setOpen(true);
  }, [isAuthenticated, user?.id]);

  const handleSync = async () => {
    if (!user?.id) return;
    setSubmitting(true);
    try {
      const inv = inventoryLocalData();
      const res = await authApi.migrate({
        favorites_passages: inv.favorites_passages,
        favorites_devotionals: inv.favorites_devotionals,
        favorites_prayers: inv.favorites_prayers,
        plan_progress: inv.plan_progress,
      });
      // Migrate journal separately (different endpoint).
      let journalCount = 0;
      if (inv.journal_entries?.length) {
        try {
          const jres = await journalApi.migrate(inv.journal_entries);
          journalCount = jres?.migrated || 0;
        } catch { /* non-fatal */ }
      }
      markSyncDone(user.id);
      const f = res?.merged?.favorites || 0;
      const p = res?.merged?.plans || 0;
      const parts = [];
      if (f) parts.push(`${f} favorito${f === 1 ? "" : "s"}`);
      if (p) parts.push(`${p} plano${p === 1 ? "" : "s"}`);
      if (journalCount) parts.push(`${journalCount} reflexão${journalCount === 1 ? "" : "ões"}`);
      toast.success("Sincronizado com sua conta!", {
        description: parts.length ? parts.join(", ") + " salvos." : "Tudo em dia.",
      });
      setOpen(false);
    } catch (err) {
      toast.error("Não foi possível sincronizar agora. Tentaremos novamente no próximo login.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (user?.id) markSyncDone(user.id);
    setOpen(false);
  };

  if (!open) return null;

  const fav =
    (snapshot.favorites_passages?.length || 0) +
    (snapshot.favorites_devotionals?.length || 0) +
    (snapshot.favorites_prayers?.length || 0);
  const plans = Object.keys(snapshot.plan_progress || {}).length;

  return (
    <div
      className="fixed inset-0 z-[60] bg-navy/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="migration-modal"
    >
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-full max-w-md p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
            <Cloud className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Sua caminhada</p>
            <h3 className="font-display text-2xl text-navy mt-1">Salvar na sua conta?</h3>
            <p className="text-navy/70 text-sm mt-2 leading-snug">
              Encontramos {fav > 0 && <strong className="text-navy">{fav} favorito{fav === 1 ? "" : "s"}</strong>}
              {fav > 0 && plans > 0 && " e "}
              {plans > 0 && <strong className="text-navy">{plans} plano{plans === 1 ? "" : "s"}</strong>}
              {" "}neste aparelho. Deseja sincronizá-los com sua conta?
            </p>
          </div>
          <button
            type="button"
            onClick={handleSkip}
            aria-label="Fechar"
            data-testid="migration-close"
            className="w-8 h-8 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center text-navy/55 hover:text-gold"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={handleSkip}
            data-testid="migration-skip"
            className="px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest text-navy/70 hover:text-navy"
          >
            Agora não
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={submitting}
            data-testid="migration-sync"
            className="px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 transition-colors"
          >
            {submitting ? "Sincronizando..." : "Sincronizar agora"}
          </button>
        </div>
      </div>
    </div>
  );
}
